import { Close, Kitchen, TableRestaurant } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { DataGrid, GridRowId } from '@mui/x-data-grid'
import React, { useEffect, useState } from 'react'
import { BillItem, ITables, KITCHEN_ITEM_STATUS } from '../../all-types'
import { CommonBtn } from '../../components'
import { useTableByIdLive } from '../../realtime'
import { setSelectedTable } from '../../redux-store'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import {
  getTableById,
  updateCartItemOfTheTable,
  updateTableItemStatus,
} from '../../services'
import { getKitchenItemStatusInfo } from '../../utils'

interface CartPanelProps {
  handleItemEdit: (productId: string, editIndex: number) => void
}

const ProductsCartItems: React.FC<CartPanelProps> = ({ handleItemEdit }) => {
  const theme = useTheme()
  const table = useAppSelector((state) => state.table.selectedTable)
  const dispatcher = useAppDispatch()

  useTableByIdLive(table?._id, (t) => dispatcher(setSelectedTable(t)))

  const isTakeAwayTable = table?._id === '999'
  const cartItems = table?.billSession?.items || []
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  )
  const [selectedItems, setSelectedItems] = useState<GridRowId[]>([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)
  const [selectedOption, setSelectedOption] = useState('')

  const refreshTableData = async () => {
    try {
      await dispatcher(getTableById(table?._id || ''))
    } catch (e) {
      console.error(e)
    }
  }

  // Calculate the total number of pages
  const totalPages = Math.ceil(cartItems.length / pageSize)

  const handleEdit = () => {
    try {
      if (selectedProductId && selectedRow) {
        handleItemEdit(selectedProductId, selectedRow - 1)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSelectedRow(null)
    }
  }

  const handleDelete = async () => {
    try {
      if (selectedRow) {
        const clonedTable: ITables = JSON.parse(JSON.stringify(table))
        clonedTable?.billSession?.items?.splice(selectedRow - 1, 1)
        if (isTakeAwayTable) {
          await dispatcher(setSelectedTable(clonedTable))
        } else {
          await dispatcher(updateCartItemOfTheTable({ table: clonedTable }))
        }
      }
    } catch (e) {
      console.log(e)
    } finally {
      setSelectedRow(null)
    }
  }

  // Handle pagination
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1)
      setSelectedRow(null)
    }
  }

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1)
      setSelectedRow(null)
    }
  }

  useEffect(() => {
    setPage(totalPages - 1)
    setSelectedRow(null)
  }, [cartItems.length])

  //TODO CAN BE USED LATER
  const sendAllMarkedItemsToKitchen = async () => {
    try {
      if (table) {
        const clonedTable: ITables = JSON.parse(JSON.stringify(table))
        const billSession = clonedTable.billSession
        const sameTime = String(new Date().getTime())
        if (billSession) {
          const items = [...(billSession.items ?? [])]
          selectedItems?.forEach((selItem) => {
            const editIndex = Number(selItem) - 1
            if (
              editIndex !== null &&
              editIndex >= 0 &&
              editIndex < items.length
            ) {
              items[editIndex] = {
                ...items[editIndex],
                kitchInfo: {
                  ...items[editIndex].kitchInfo,
                  status: KITCHEN_ITEM_STATUS.IN_KITCHEN,
                  itemReceviedTime: sameTime,
                },
              }
            }
          })

          clonedTable.billSession = {
            ...billSession,
            items,
          }

          await dispatcher(updateCartItemOfTheTable({ table: clonedTable }))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  //NEW
  const senItemToKitchen = async (status: KITCHEN_ITEM_STATUS) => {
    try {
      if (table && selectedRow) {
        const editIndex = selectedRow - 1
        const clonedTable: ITables = JSON.parse(JSON.stringify(table))
        const billSession = clonedTable.billSession
        if (billSession) {
          const items = [...(billSession.items ?? [])]
          if (
            editIndex !== null &&
            editIndex >= 0 &&
            editIndex < items.length
          ) {
            await dispatcher(
              updateTableItemStatus({
                tableId: table?._id || '',
                sno: items[editIndex].sno || 0,
                status,
              })
            )
          }
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSelectedRow(null)
    }
  }

  const columns = [
    {
      field: 'id',
      headerName: 'Cart',
      flex: 0.2,
      components: {
        Header: () => {
          return null
        },
      },
      renderCell: (params: any) => {
        const product: BillItem = params.row?.product
        return (
          <Stack flexDirection={'row'} flex={1} gap={2} height={'100%'}>
            <Stack flex={2} justifyContent={'center'}>
              <Stack
                height={50}
                sx={{
                  backgroundImage: `url(${product?.itemImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: 2,
                }}
              ></Stack>
            </Stack>
            <Stack flex={8} justifyContent={'center'}>
              <Stack>
                <Typography variant="body1">{product.itemName}</Typography>
              </Stack>
              <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <Stack>
                  <Stack flexDirection={'row'} alignItems={'center'} gap={2}>
                    <Typography variant="body2">{`${product.quantity} ${product.quantityType}  `}</Typography>
                    <Typography variant="body2">
                      {` x ${product.noOfItems}`}{' '}
                    </Typography>
                  </Stack>
                  <Stack>
                    {/* <Typography variant='body2'>{`${product?.kitchInfo?.status || "In Cart"}`} </Typography> */}
                  </Stack>
                </Stack>
                <Stack>
                  <Typography variant="body2" fontWeight={'bold'}>
                    {product.amount}
                  </Typography>
                  <Chip
                    color={
                      (getKitchenItemStatusInfo(product?.kitchInfo?.status)
                        ?.color as any) || 'default'
                    }
                    size={'small'}
                    label={`${getKitchenItemStatusInfo(product?.kitchInfo?.status)?.displayText || 'In Cart'}`}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )
      },
    },
    // { field: 'itemName', headerName: 'Item', flex: 4 },
    // { field: 'quantity', headerName: 'Qty', flex: 1.5 },
    // { field: 'amount', headerName: 'Amount', flex: 1.5 },
  ]

  const rows = cartItems
    ?.slice(page * pageSize, page * pageSize + pageSize) // Paginate the rows
    .map((item, index) => ({
      id: page * pageSize + index + 1, // Adjust index based on pagination
      itemName: item.itemName,
      quantity: item.quantity,
      amount: item.amount,
      productId: item.productId,
      product: item,
    }))

  const REDUCE = 40
  const ROW_HEIGHT = 120 - REDUCE

  const canShowSendInKitchen = (selectedRow: number) =>
    cartItems[selectedRow - 1] &&
    cartItems[selectedRow - 1]?.kitchInfo?.status ===
      KITCHEN_ITEM_STATUS.IN_CART

  const canShowServed = (selectedRow: number) =>
    cartItems[selectedRow - 1] &&
    (cartItems[selectedRow - 1]?.kitchInfo?.status ===
      KITCHEN_ITEM_STATUS.READY ||
      cartItems[selectedRow - 1]?.kitchInfo?.status ===
        KITCHEN_ITEM_STATUS.IN_CART)

  return (
    <Stack flex={1} sx={{ position: 'relative', width: '100%' }}>
      {/* Custom Pagination Controls */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 1 }}
      >
        {/* <RefreshComponent onRefresh={refreshTableData} /> */}
        <Button
          onClick={handlePrevPage}
          disabled={page === 0}
          variant="contained"
          sx={{ width: 50, height: 50 }}
          startIcon={
            <KeyboardArrowLeftIcon
              fontSize="large"
              sx={{ color: 'white', marginLeft: 1 }}
            />
          }
        />

        <Typography sx={{ alignSelf: 'center' }}>
          {page + 1} of {totalPages}
        </Typography>
        <Button
          onClick={handleNextPage}
          disabled={page >= totalPages - 1}
          variant="contained"
          sx={{ width: 50, height: 50 }}
          startIcon={
            <KeyboardArrowRightIcon
              fontSize="large"
              sx={{ color: 'white', marginLeft: 1 }}
            />
          }
        />
        {/* <CommonBtn
                    onClick={handleNextPage}
                    disabled={page >= totalPages - 1}
                    variant="contained"
                >
                    Next
                </CommonBtn> */}
      </Box>

      <Stack>
        <DataGrid
          disableColumnResize
          rows={rows}
          columns={columns}
          rowHeight={ROW_HEIGHT}
          disableColumnSorting
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[pageSize]} // Disable internal pagination
          hideFooterPagination // Hide the default pagination
          onRowClick={(params) => {
            setSelectedRow(params.row.id)
            setSelectedProductId(params.row.productId)
          }}
          sx={{
            maxWidth: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
              border: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none', // Hides pagination footer and removes its space
            },
            '& .MuiDataGrid-columnHeader': {
              display: 'none',
            },
            '& .MuiDataGrid-scrollbar': {
              display: 'none',
            },
          }}
        />
      </Stack>

      <Stack flex={1} justifyContent={'flex-end'}>
        {selectedItems?.length > 0 && (
          <CommonBtn variant="contained" onClick={sendAllMarkedItemsToKitchen}>
            Send To Kitchen
          </CommonBtn>
        )}
      </Stack>
      {/* <Stack>

        <DataGrid
          disableColumnResize
          checkboxSelection
          onRowSelectionModelChange={(t, d) => setSelectedItems(Object.values(t))}
          rows={rows}
          columns={columns}
          rowHeight={ROW_HEIGHT}
          disableColumnSorting
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[pageSize]} // Disable internal pagination
          hideFooterPagination // Hide the default pagination
          // onRowClick={(params) => {
          //   setSelectedRow(params.row.id)
          //   setSelectedProductId(params.row.productId)
          // }}
          sx={{
            maxWidth: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
              border: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none', // Hides pagination footer and removes its space
            },
            '& .MuiDataGrid-columnHeader': {
              display: 'none',
            },
            '& .MuiDataGrid-scrollbar': {
              display: 'none',
            },
          }}
        />
      </Stack> */}
      {selectedRow !== null && (
        <Box
          sx={{
            position: 'absolute',
            top: `${
              (selectedRow > 5
                ? (selectedRow - 5 * page) * ROW_HEIGHT
                : selectedRow * ROW_HEIGHT) -
              (54 - REDUCE)
            }px`,
            left: 0,
            width: '100%',
            height: `${ROW_HEIGHT}px`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            zIndex: 1,
          }}
        >
          {/* Select Dropdown here */}
          {(canShowSendInKitchen(selectedRow) ||
            canShowServed(selectedRow)) && (
            <Select
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {canShowSendInKitchen(selectedRow) && (
                <MenuItem value="option1">
                  <CommonBtn
                    variant="contained"
                    startIcon={<Kitchen />}
                    color="info"
                    onClick={() =>
                      senItemToKitchen(KITCHEN_ITEM_STATUS.IN_KITCHEN)
                    }
                  >
                    Send to Kitchen
                  </CommonBtn>
                </MenuItem>
              )}

              {canShowServed(selectedRow) && (
                <MenuItem value="option2">
                  <CommonBtn
                    variant="contained"
                    startIcon={<TableRestaurant />}
                    color="warning"
                    onClick={() => senItemToKitchen(KITCHEN_ITEM_STATUS.SERVED)}
                  >
                    Served
                  </CommonBtn>
                </MenuItem>
              )}
            </Select>
          )}
          {cartItems[selectedRow - 1] &&
            (cartItems[selectedRow - 1]?.kitchInfo?.status ===
              KITCHEN_ITEM_STATUS.IN_CART ||
              cartItems[selectedRow - 1]?.kitchInfo?.status ===
                KITCHEN_ITEM_STATUS.IN_KITCHEN) && (
              <>
                <IconButton
                  aria-label="edit"
                  size="large"
                  color="info"
                  onClick={handleEdit}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </>
            )}
          <IconButton
            aria-label="delete"
            size="large"
            color="error"
            onClick={handleDelete}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>

          <IconButton
            aria-label="close"
            size="large"
            color="error"
            onClick={() => setSelectedRow(null)}
          >
            <Close fontSize="inherit" />
          </IconButton>
        </Box>
      )}
    </Stack>
  )
}

export { ProductsCartItems }
